import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import { onInput, readLastInput, recordInput, WARNING_CLASS, watchInput } from '@/services/session/activity';
import { currentLimits, nextChangeAt, type SessionState } from '@/services/session/limits';

/** Check at least this often while nothing is due, and every second while the warning shows. */
const SLOW_CHECK_MS = 15_000;
const WARNING_CHECK_MS = 1000;

function sameState(a: SessionState | null, b: SessionState | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind !== b.kind) return false;
  return a.kind !== 'warning' || (b.kind === 'warning' && a.endsAt === b.endsAt);
}

function clockTime(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function duration(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

/**
 * Signs the person out after 2 hours with no input in any portal tab, and
 * warns 2 minutes before (TASK-649). Mounted only while signed in.
 *
 * Every check reads the wall clock and the shared time of the last input, so
 * a tab the browser has slowed down, or a laptop that wakes, acts on the
 * right answer as soon as it runs. The time in the warning is a clock time,
 * so a slowed tab never shows a stale countdown.
 */
export default function SessionGuard({
  isCounted,
}: {
  /** Which events count as a person's input. Tests only; the default is the browser's own mark. */
  isCounted?: (event: Event) => boolean;
} = {}) {
  const { signOut } = useAuth();
  const [state, setState] = useState<SessionState | null>(() => authService.getSessionState());

  // Count this tab's input.
  useEffect(() => watchInput(isCounted), [isCounted]);

  useEffect(() => {
    let timer: number | undefined;
    const check = () => {
      window.clearTimeout(timer);
      // Reading the state ends the session if a limit has passed.
      const next = authService.getSessionState();
      setState((prev) => (sameState(prev, next) ? prev : next));
      if (!next || next.kind === 'ended') return;
      const wait =
        next.kind === 'warning'
          ? WARNING_CHECK_MS
          : Math.min(
              SLOW_CHECK_MS,
              Math.max(250, nextChangeAt(next, readLastInput() ?? Date.now(), currentLimits()) - Date.now())
            );
      timer = window.setTimeout(check, wait);
    };
    check();
    const stopInput = onInput(check);
    const onReturn = () => check();
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    window.addEventListener('pageshow', onReturn);
    return () => {
      window.clearTimeout(timer);
      stopInput();
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
      window.removeEventListener('pageshow', onReturn);
    };
  }, []);

  const warning = state?.kind === 'warning' ? state : null;
  const idleText = duration(currentLimits().idleMs);

  return (
    <Modal
      show={warning !== null}
      backdrop="static"
      keyboard={false}
      centered
      // Input inside the warning is not input: a key or click there must reach
      // the button, not close the warning first (see watchInput).
      dialogClassName={WARNING_CLASS}
      aria-labelledby="session-warning-title"
      aria-describedby="session-warning-body"
    >
      <Modal.Header>
        <Modal.Title id="session-warning-title">You will be signed out soon</Modal.Title>
      </Modal.Header>
      <Modal.Body id="session-warning-body">
        There was no input in the portal for almost {idleText}. The portal signs you out at{' '}
        <strong>{warning ? clockTime(warning.endsAt) : ''}</strong>.
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => void signOut()}>
          Sign out now
        </Button>
        <Button variant="primary" autoFocus onClick={() => recordInput(Date.now(), true)}>
          Stay signed in
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
